import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { tokenStore } from '@/lib/auth';

export const Route = createFileRoute('/auth/callback')({ component: AuthCallback });

/**
 * Endpoint usado pelo backend após Google OAuth.
 * Lê access/refresh do hash, persiste e redireciona.
 *
 * NB sobre StrictMode em dev: o useEffect roda duas vezes. Sem cuidado, na
 * segunda execução o hash já foi limpo e o componente caía em "/login".
 * Por isso processamos o hash síncronamente no escopo do módulo (uma só vez)
 * — assim a segunda chamada do effect só dispara o navigate.
 */

let processed = false;
function readTokensFromHash(): { next: string } | null {
  if (typeof window === 'undefined') return null;
  if (processed) {
    return tokenStore.getAccess() ? { next: '/dashboard' } : null;
  }
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return tokenStore.getAccess() ? { next: '/dashboard' } : null;

  const params = new URLSearchParams(hash);
  const access = params.get('access');
  const refresh = params.get('refresh');
  const next = params.get('next') ?? '/dashboard';

  if (access && refresh) {
    tokenStore.set(access, refresh);
    window.history.replaceState({}, '', window.location.pathname);
    processed = true;
    return { next };
  }
  return null;
}

function AuthCallback() {
  const navigate = useNavigate();

  useEffect(() => {
    const result = readTokensFromHash();
    if (result) {
      // window.location.href (não navigate) porque o `next` pode conter
      // query string (ex: /dashboard/mensagens/novo?therapistId=X). O
      // navigate do TanStack Router só aceita `to` sem query — se você
      // passar tudo junto, ele trata como parte do path e cai em 404.
      // Full page reload também garante que hooks (authApi.me etc)
      // rehidratam com o token recém-salvo.
      window.location.href = result.next;
    } else {
      navigate({ to: '/login' });
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FBF8F3]">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <div className="size-8 border-4 border-teal-300 border-t-teal-500 rounded-full animate-spin" />
        <p className="text-sm">Concluindo seu acesso...</p>
      </div>
    </div>
  );
}
